import { envValue, type EnvRecord } from "./envPreflight";

/**
 * TA-1319 fix 2 (re-review finding 8): the one list of outside connections
 * the demo's marker may name.
 *
 * The demo's env builder (apps/web demo/demoAgents.ts) names what it
 * simulates in ACKRA_MOCK_CONNECTIONS. Two readers read it: the worker, which
 * wires a mock for each name and refuses any other (apps/worker main.ts
 * `planMockConnections`), and the dashboard, which reads each named
 * connection as connected (apps/web lib/agentStatus.ts `mockedConnections`).
 * Both take the names from here, so the dashboard can never call a
 * connection connected that the worker has nothing wired behind (section 0).
 * apps/web lib/mockConnectionsParity.test.ts runs both readers over the same
 * names.
 *
 *  - instagram: customer lines arrive through the real webhook parser.
 *  - messaging: approved replies are held in the dashboard, never transmitted.
 *  - profile: the gateway's mock profile source (researchWiring.ts).
 *  - calendar: the gateway's mock calendar (graph/runtime.ts).
 *
 * Adding a name here is a promise that the worker wires something for it.
 * Nothing in production sets the marker, and the worker refuses it under
 * NODE_ENV=production.
 */
export const MOCK_CONNECTIONS_ENV = "ACKRA_MOCK_CONNECTIONS";

export const MOCK_CONNECTION_NAMES = Object.freeze(["instagram", "messaging", "profile", "calendar"] as const);

export type MockConnectionName = (typeof MOCK_CONNECTION_NAMES)[number];

export function isMockConnectionName(name: string): name is MockConnectionName {
  return (MOCK_CONNECTION_NAMES as readonly string[]).includes(name);
}

/**
 * The practice rail marker (Mike's trial, 2026-10-16). `pnpm rail:sim` hands it
 * to the WEB and WORKER processes, and only in its simulator mode, where a
 * stand-in for Meta on the same machine receives every reply (apps/web
 * demo/railSim.ts `railSimChildEnvs`). That worker may transmit, to the
 * stand-in, so it never carries the mock marker above (TA-1319), and this is
 * its own name for that reason. On the web it opens the owner's test console
 * (apps/web lib/consoleGate.ts) and lets the dashboard say an agent is working
 * on practice messages; it never makes a connection read as connected. On the
 * worker it does one thing: the review cycle reads the practice reviews the
 * console stored (`PRACTICE_REVIEW_ID_PREFIX`) instead of a review site
 * (apps/worker practiceReviewsPlan.ts, which refuses it under production and
 * beside a Graph that is not a simulator). Exactly "simulator" is on. Nothing
 * in production sets it, and both readers refuse under production first.
 */
export const PRACTICE_RAIL_ENV = "ACKRA_PRACTICE_RAIL";
export const PRACTICE_RAIL_SIMULATOR = "simulator";

/**
 * The external id prefix of a review the owner's test console writes
 * (apps/web lib/consoleReview.ts). The practice review stand-in reads only
 * reviews whose id starts with it, for the asking shop: never a seeded
 * review, never another shop's.
 */
export const PRACTICE_REVIEW_ID_PREFIX = "console-";

export function isPracticeRail(env: EnvRecord): boolean {
  return envValue(env, PRACTICE_RAIL_ENV) === PRACTICE_RAIL_SIMULATOR;
}
